import FinancingIcon from "app/assets/IconComponent/FinancingIcon";
import AvatarWithName from "v2/common/AvatarWithName";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";

interface FinancingRow {
  id: string;
  lenderName: string;
  lenderLogo?: string;
  financingNumber: string;
  borrowerName: string;
  borrowerLogo?: string;
  interestTreatment: string;
  initialRate: string;
  financingValue: string;
  expectedPayoff: string;
}

const MOCK_FINANCING: FinancingRow[] = [
  {
    id: "1",
    lenderName: "Main St. Financial",
    lenderLogo: "",
    financingNumber: "4499887665",
    borrowerName: "ABC Logistic, Inc.",
    borrowerLogo: "",
    interestTreatment: "Out-of-Pocket",
    initialRate: "5.000%",
    financingValue: "$179K",
    expectedPayoff: "Sep 14, 2035",
  },
];

const COLUMNS: { key: keyof FinancingRow; label: string; width: string }[] = [
  { key: "lenderName", label: "Lender", width: "18%" },
  { key: "financingNumber", label: "Financing #", width: "14%" },
  { key: "borrowerName", label: "Borrower", width: "18%" },
  { key: "interestTreatment", label: "Interest Treatment", width: "16%" },
  { key: "initialRate", label: "Initial Rate", width: "12%" },
  { key: "financingValue", label: "Financing Value", width: "12%" },
  { key: "expectedPayoff", label: "Expected Payoff", width: "14%" },
];

export type { FinancingRow };

interface FinancingCardProps {
  rows?: FinancingRow[];
}

const FinancingCard = ({ rows }: FinancingCardProps = {}) => {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const tableData = rows ?? MOCK_FINANCING;

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "border-[#2E2E42]" : "border-[#F1F5F9]";
  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";

  const renderCell = (row: FinancingRow, col: (typeof COLUMNS)[number]) => {
    switch (col.key) {
      case "lenderName":
        return (
          <AvatarWithName
            imageUrl={row.lenderLogo}
            name={row.lenderName}
            tooltipText={row.lenderName.length > 20 ? row.lenderName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName="font-medium text-[14px]"
          />
        );
      case "borrowerName":
        return (
          <AvatarWithName
            imageUrl={row.borrowerLogo}
            name={row.borrowerName}
            tooltipText={row.borrowerName.length > 20 ? row.borrowerName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName="font-medium text-[14px]"
          />
        );
      case "interestTreatment":
      case "initialRate":
      case "financingValue":
        return <span className={`text-[14px] font-bold ${tdText}`}>{row[col.key]}</span>;
      default:
        return <span className={`text-[14px] ${tdText}`}>{row[col.key] ?? "—"}</span>;
    }
  };

  return (
    <div className={`w-full rounded-2xl shadow-lg mt-4 ${cardBg}`}>
      {/* Header */}
      <p
        className={`text-[13px] font-semibold conthrax-font p-4 flex items-center gap-2 bg-[#f5f5f5] ${headerText}`}
      >
        <FinancingIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} />
        Financing
      </p>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={thBg}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={`px-4 py-3 text-left text-[14px] font-medium uppercase tracking-wider whitespace-nowrap ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr
                key={row.id}
                className={`transition-colors hover:${isDark ? "bg-[#1E1E2E]" : "bg-[#F8FAFC]"} ${
                  index < tableData.length - 1 ? `border-b ${divider}` : ""
                }`}
              >
                {COLUMNS.map((col) => (
                  <td key={col.key} className="px-4 py-4 border-b border-b-gray-900">
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancingCard;
